import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  Description as DocumentIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { DocumentService, RelevantDocument, EmailRequest } from '../services/documentService';
import { Client } from '../types/client';

interface RelevantDocumentsProps {
  client: Client;
  onEmailSent?: () => void;
}

const RelevantDocuments: React.FC<RelevantDocumentsProps> = ({ client, onEmailSent }) => {
  const [documents, setDocuments] = useState<RelevantDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState<number | null>(null);
  const [emailDialog, setEmailDialog] = useState<{ open: boolean; document: RelevantDocument | null }>({
    open: false,
    document: null
  });

  const loadRelevantDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const relevantDocs = await DocumentService.getRelevantDocuments(client.id);
      setDocuments(relevantDocs);
    } catch (err) {
      setError('Failed to load relevant documents: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    if (client.id) {
      loadRelevantDocuments();
    }
  }, [client.id, loadRelevantDocuments]);

  const handleSendEmail = async (document: RelevantDocument) => {
    setEmailDialog({ open: true, document });
  };

  const confirmSendEmail = async () => {
    if (!emailDialog.document) return;

    try {
      setSendingEmail(emailDialog.document.id);
      
      const emailRequest: EmailRequest = {
        client_id: client.id,
        document_id: emailDialog.document.id,
        client_email: client.email,
        client_name: `${client.first_name} ${client.last_name}`,
        language: 'English' // TODO: Add language selection
      };

      await DocumentService.sendDocumentEmail(emailRequest);
      
      setEmailDialog({ open: false, document: null });
      setSendingEmail(null);
      
      // Show success message
      setError(null);
      onEmailSent?.();
      
    } catch (err) {
      setError('Failed to send email: ' + (err as Error).message);
      setSendingEmail(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRelevanceTags = (document: RelevantDocument) => {
    const tags = [];
    
    if (document.company_name) {
      tags.push(
        <Chip
          key="company"
          icon={<BusinessIcon />}
          label={document.company_name}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }
    
    if (document.sector_name) {
      tags.push(
        <Chip
          key="sector"
          icon={<CategoryIcon />}
          label={document.sector_name}
          size="small"
          color="secondary"
          variant="outlined"
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }
    
    if (document.analyst_name) {
      tags.push(
        <Chip
          key="analyst"
          icon={<PersonIcon />}
          label={document.analyst_name}
          size="small"
          color="default"
          variant="outlined"
          sx={{ mr: 1, mb: 1 }}
        />
      );
    }
    
    return tags;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <DocumentIcon sx={{ mr: 1 }} />
        Relevant Documents
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {documents.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary" textAlign="center">
              No relevant documents found for this client's interests.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {documents.map((document, index) => (
            <React.Fragment key={document.id}>
              <ListItem sx={{ 
                flexDirection: 'column', 
                alignItems: 'flex-start',
                bgcolor: 'background.paper',
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" component="div" gutterBottom>
                      {document.title}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CalendarIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="textSecondary">
                        {formatDate(document.created_at)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                      {getRelevanceTags(document)}
                    </Box>
                  </Box>
                  
                  <ListItemSecondaryAction>
                    <Button
                      variant="contained"
                      startIcon={<EmailIcon />}
                      onClick={() => handleSendEmail(document)}
                      disabled={sendingEmail === document.id}
                      size="small"
                      sx={{ minWidth: 'auto' }}
                    >
                      {sendingEmail === document.id ? (
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                      ) : (
                        'Send Email'
                      )}
                    </Button>
                  </ListItemSecondaryAction>
                </Box>
              </ListItem>
              
              {index < documents.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Email Confirmation Dialog */}
      <Dialog 
        open={emailDialog.open} 
        onClose={() => setEmailDialog({ open: false, document: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Research Summary Email</DialogTitle>
        <DialogContent>
          {emailDialog.document && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Send a professional summary of the following document to{' '}
                <strong>{client.first_name} {client.last_name}</strong> ({client.email})?
              </Typography>
              
              <Card sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  {emailDialog.document.title}
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {getRelevanceTags(emailDialog.document)}
                </Box>
                
                <Typography variant="body2" color="textSecondary">
                  Published: {formatDate(emailDialog.document.created_at)}
                </Typography>
              </Card>
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                The email will include a professional summary generated using AI, similar to the PDF AI Chat functionality.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEmailDialog({ open: false, document: null })}
            disabled={sendingEmail !== null}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmSendEmail}
            variant="contained"
            disabled={sendingEmail !== null}
            startIcon={sendingEmail !== null ? <CircularProgress size={16} /> : <EmailIcon />}
          >
            {sendingEmail !== null ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RelevantDocuments; 